"use client";

import { useEffect, useMemo, useState, useTransition } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { ListFilter, RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import Button from "@/components/ui/Button";
import { VENDOR_ADDRESS_CITY_FILTERS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export type VendorsSortKey = "cars" | "rating" | "name" | "newest";

function buildVendorsSearch(params: {
  q: string;
  city: string;
  sort: VendorsSortKey;
  verified: boolean;
}): string {
  const sp = new URLSearchParams();
  const q = params.q.trim();
  if (q) sp.set("q", q);
  if (params.city) sp.set("city", params.city);
  if (params.sort !== "cars") sp.set("sort", params.sort);
  if (params.verified) sp.set("verified", "1");
  return sp.toString();
}

function countActiveVendorsFilters(
  q: string,
  city: string,
  sort: VendorsSortKey,
  verified: boolean,
): number {
  let n = 0;
  if (q.trim()) n += 1;
  if (city) n += 1;
  if (verified) n += 1;
  if (sort !== "cars") n += 1;
  return n;
}

export default function VendorsFilters({
  initialQ,
  initialVerified,
  initialCity,
  initialSort,
}: {
  initialQ: string;
  initialVerified: boolean;
  initialCity: string;
  initialSort: VendorsSortKey;
}) {
  const t = useTranslations("VendorsPage");
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [sheetOpen, setSheetOpen] = useState(false);

  const [q, setQ] = useState(initialQ);
  const [city, setCity] = useState(initialCity);
  const [sort, setSort] = useState<VendorsSortKey>(initialSort);
  const [verified, setVerified] = useState(initialVerified);

  useEffect(() => {
    setQ(initialQ);
    setCity(initialCity);
    setSort(initialSort);
    setVerified(initialVerified);
  }, [initialQ, initialCity, initialSort, initialVerified]);

  useEffect(() => {
    if (!sheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheetOpen]);

  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSheetOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheetOpen]);

  const activeCount = useMemo(
    () => countActiveVendorsFilters(q, city, sort, verified),
    [q, city, sort, verified],
  );

  function apply() {
    const qs = buildVendorsSearch({ q, city, sort, verified });
    startTransition(() => {
      router.replace(qs ? `/vendors?${qs}` : "/vendors", { scroll: false });
    });
  }

  function reset() {
    setSheetOpen(false);
    setQ("");
    setCity("");
    setSort("cars");
    setVerified(false);
    startTransition(() => {
      router.replace("/vendors", { scroll: false });
    });
  }

  function applyAndCloseSheet() {
    apply();
    setSheetOpen(false);
  }

  const filterFields = (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-gray-500 block mb-1 font-medium">{t("searchLabel")}</label>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("searchPlaceholder")}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 bg-white"
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1 font-medium">{t("cityLabel")}</label>
        <select
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none bg-white cursor-pointer focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15"
        >
          <option value="">{t("cityAny")}</option>
          {VENDOR_ADDRESS_CITY_FILTERS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1 font-medium">{t("sortLabel")}</label>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as VendorsSortKey)}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none bg-white cursor-pointer focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15"
        >
          <option value="cars">{t("sortCars")}</option>
          <option value="rating">{t("sortRating")}</option>
          <option value="name">{t("sortName")}</option>
          <option value="newest">{t("sortNewest")}</option>
        </select>
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 py-1">
        <input
          type="checkbox"
          checked={verified}
          onChange={(e) => setVerified(e.target.checked)}
          className="rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]"
        />
        {t("verifiedOnly")}
      </label>
    </div>
  );

  return (
    <>
      <div className="hidden lg:block relative">
        {isPending ? (
          <div className="absolute inset-0 z-10 rounded-2xl bg-white/60 flex items-center justify-center pointer-events-none">
            <span className="text-sm font-medium text-gray-600">{t("apply")}…</span>
          </div>
        ) : null}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 space-y-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">{t("filtersHeading")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 lg:gap-4">
            <div className="md:col-span-1 lg:col-span-4">
              <label className="text-xs text-gray-500 block mb-1 font-medium">{t("searchLabel")}</label>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/10"
              />
            </div>
            <div className="md:col-span-1 lg:col-span-3">
              <label className="text-xs text-gray-500 block mb-1 font-medium">{t("cityLabel")}</label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none bg-white cursor-pointer focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/10"
              >
                <option value="">{t("cityAny")}</option>
                {VENDOR_ADDRESS_CITY_FILTERS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1 lg:col-span-3">
              <label className="text-xs text-gray-500 block mb-1 font-medium">{t("sortLabel")}</label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as VendorsSortKey)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none bg-white cursor-pointer focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/10"
              >
                <option value="cars">{t("sortCars")}</option>
                <option value="rating">{t("sortRating")}</option>
                <option value="name">{t("sortName")}</option>
                <option value="newest">{t("sortNewest")}</option>
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-2 flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 min-h-[42px]">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={(e) => setVerified(e.target.checked)}
                  className="rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]"
                />
                {t("verifiedOnly")}
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" className="gap-1.5" onClick={apply} loading={isPending}>
              <Search size={14} />
              {t("apply")}
            </Button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
            >
              <RotateCcw size={14} />
              {t("reset")}
            </button>
          </div>
        </div>
      </div>

      <div className="lg:hidden w-full shrink-0">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={cn(
            "w-full flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5",
            "text-left shadow-sm active:scale-[0.99] transition-transform",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:ring-offset-2",
          )}
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
        >
          <span className="flex items-center gap-3 min-w-0">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E63946]/10 text-[#E63946] shrink-0">
              <SlidersHorizontal size={20} strokeWidth={2} aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-gray-900">{t("openFilters")}</span>
              <span className="block text-xs text-gray-500 truncate">
                {activeCount > 0 ? t("activeFiltersCount", { count: activeCount }) : t("mobileFiltersHint")}
              </span>
            </span>
          </span>
          {activeCount > 0 ? (
            <span className="flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-[#E63946] px-2 text-xs font-bold text-white shrink-0">
              {activeCount}
            </span>
          ) : (
            <ListFilter size={18} className="text-gray-400 shrink-0" aria-hidden />
          )}
        </button>
      </div>

      {sheetOpen ? (
        <div
          className="lg:hidden fixed inset-0 z-[60] flex flex-col justify-end"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vendors-filters-sheet-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
            aria-label={t("sheetCloseAria")}
            onClick={() => setSheetOpen(false)}
          />
          <div className="relative flex max-h-[min(92dvh,920px)] flex-col rounded-t-[1.25rem] bg-white shadow-[0_-12px_40px_rgba(0,0,0,0.12)] animate-[catalog-sheet-in_0.28s_cubic-bezier(0.32,0.72,0,1)_forwards]">
            <div className="flex justify-center pt-2 pb-1 shrink-0" aria-hidden>
              <div className="h-1 w-10 rounded-full bg-gray-200" />
            </div>
            <div className="flex items-center justify-between gap-3 px-4 pb-3 border-b border-gray-100 shrink-0">
              <div className="min-w-0">
                <h2 id="vendors-filters-sheet-title" className="text-base font-bold text-gray-900 truncate">
                  {t("sheetTitle")}
                </h2>
                {activeCount > 0 ? (
                  <p className="text-xs text-gray-500 mt-0.5">{t("activeFiltersCount", { count: activeCount })}</p>
                ) : null}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={reset}
                  className="text-xs font-medium text-[#E63946] hover:underline px-2 py-1"
                >
                  {t("reset")}
                </button>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                  aria-label={t("sheetCloseAria")}
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto overscroll-contain flex-1 min-h-0 px-4 pt-4 pb-2">
              {filterFields}
            </div>

            <div className="shrink-0 border-t border-gray-100 bg-white px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-2">
              <Button
                type="button"
                size="lg"
                className="w-full gap-2"
                loading={isPending}
                onClick={applyAndCloseSheet}
              >
                <Search size={18} />
                {t("sheetDone")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
